import type { Dayjs } from "dayjs";
import { isEqual, isNil } from "lodash";
import { definitionFor } from "shared/company-settings/helpers";
import {
  CLEARABLE_SETTING_KEYS,
  COMPANY_SETTING_KEYS,
  type CompanySettingKey,
} from "shared/company-settings/schema";
import {
  parseNumberEntry,
  validateHttpUrl,
} from "shared/company-settings/validate-setting-value";
import type {
  MeCompanyConfigurationFragment,
  UpdateCompanyConfigurationInput,
} from "../../generated/graphql";

/**
 * Registry keys `CompanyConfigurationEntity` does not expose. The three are
 * undecorated on the entity on purpose -- `debounceAccessorialSearch` and
 * `debounceContactSearch` are writable but never read back, and
 * `t2tDocumentPropagationAllowlist` is blob-only and read through
 * `getEffectiveT2tDocumentPropagationAllowlist`. Selecting any of them fails
 * codegen, so the fragment cannot carry them and the editor has no value to
 * show or diff against.
 */
export const UNSELECTABLE_SETTING_KEYS = [
  "debounceAccessorialSearch",
  "debounceContactSearch",
  "t2tDocumentPropagationAllowlist",
] as const;

export type SelectableSettingKey = Exclude<
  CompanySettingKey,
  (typeof UNSELECTABLE_SETTING_KEYS)[number]
>;

const UNSELECTABLE: ReadonlySet<string> = new Set(UNSELECTABLE_SETTING_KEYS);

export const isSelectableSettingKey = (
  key: CompanySettingKey,
): key is SelectableSettingKey => !UNSELECTABLE.has(key);

/**
 * The draft value of the setting a multi-select's `gatedOptions` names, so the
 * row offers the gated options as soon as the gate is switched on.
 */
export const gateValueFor = (
  settingKey: SelectableSettingKey,
  draft: UpdateCompanyConfigurationInput,
): unknown => {
  const { control } = definitionFor(settingKey);
  if (control.type !== "multi-select" || control.gatedOptions === undefined) {
    return undefined;
  }
  const gateKey = control.gatedOptions.key;
  return isSelectableSettingKey(gateKey) ? draft[gateKey] : undefined;
};

/** Every configuration key the fragment selects, in registry order. */
export const SELECTABLE_SETTING_KEYS: readonly SelectableSettingKey[] =
  COMPANY_SETTING_KEYS.filter((key) => isSelectableSettingKey(key));

/**
 * What the operator has typed into each `number` box they touched, kept as text
 * so a half-typed or out-of-bounds entry stays on screen with its error instead
 * of being coerced into the draft.
 */
export type NumberEntries = Partial<Record<SelectableSettingKey, string>>;

/**
 * A value arrives as `unknown` -- one draft carries ninety-one settings of
 * different shapes -- so each is narrowed before it reaches an input rather
 * than handed to `String`, which would put `[object Object]` in the box.
 */
export const asText = (value: unknown): string => {
  if (isNil(value)) {
    return "";
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return JSON.stringify(value) ?? "";
};

/**
 * A multi-select reads its own value back, so an entry of any other shape --
 * a stored value the registry no longer describes -- is dropped rather than
 * rendered as a chip the operator cannot remove.
 */
export const asStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === "string")
    : [];

/**
 * What a time control commits. The blob stores a timestamp as an ISO string,
 * so a `Dayjs` would have to survive JSON serialisation to arrive intact, and
 * the diff against the fetched string would never match. Seconds and
 * milliseconds are zeroed because the picker only offers minutes; without that
 * every save would differ from the last by the moment it was opened.
 */
export const toCommittedTimeValue = (
  next: Dayjs | null | undefined,
): string | null =>
  isNil(next) || !next.isValid()
    ? null
    : next.set("second", 0).set("millisecond", 0).toISOString();

/**
 * Converts what the operator typed into the value the mutation should carry.
 * An emptied box means "clear it", which the server only accepts as `null`:
 * `@IsOptional()` short-circuits on null and undefined only, so `""` is a
 * validation error and would fail the whole save rather than clearing.
 */
export const toUpdatedUrlValue = (rawInput: string): string | null =>
  rawInput === "" ? null : rawInput;

/**
 * Every drafted value the server would refuse, keyed by setting: a `url`
 * control's value, or the text still in a `number` box that did not parse. An
 * empty result means the draft can be submitted.
 */
export const findConfigurationErrors = (
  configuration: Record<string, unknown>,
  numberEntries: NumberEntries,
): Partial<Record<string, string>> => {
  const errors: Partial<Record<string, string>> = {};
  for (const key of SELECTABLE_SETTING_KEYS) {
    const { control } = definitionFor(key);
    if (control.type === "number") {
      const entry = numberEntries[key];
      if (entry === undefined) {
        continue;
      }
      const parsed = parseNumberEntry(
        entry,
        control,
        CLEARABLE_SETTING_KEYS.has(key),
      );
      if (!parsed.ok) {
        errors[key] = parsed.error;
      }
      continue;
    }
    if (control.type !== "url") {
      continue;
    }
    const value = configuration[key];
    if (typeof value !== "string" && !isNil(value)) {
      continue;
    }
    const error = validateHttpUrl(value);
    if (error !== undefined) {
      errors[key] = error;
    }
  }
  return errors;
};

/**
 * The values the editor read back, which is every selectable key once a
 * company is loaded. `Pick` is what ties this to the fragment: a registry key
 * the fragment stops selecting stops compiling here rather than silently
 * dropping out of the diff.
 */
export type FetchedConfiguration = Partial<
  Pick<MeCompanyConfigurationFragment, SelectableSettingKey>
>;

/** Keys whose drafted value differs from the one that was fetched. */
export const changedConfigurationKeys = (
  fetched: FetchedConfiguration,
  draft: UpdateCompanyConfigurationInput,
): readonly SelectableSettingKey[] =>
  SELECTABLE_SETTING_KEYS.filter((key) => !isEqual(fetched[key], draft[key]));

const copyValue = <Key extends SelectableSettingKey>(
  key: Key,
  draft: UpdateCompanyConfigurationInput,
  into: UpdateCompanyConfigurationInput,
): void => {
  into[key] = draft[key];
};

/**
 * The mutation input carrying the changed keys only, so the settings change
 * log records the edit instead of ninety-one no-op writes beside it.
 */
export const buildConfigurationUpdate = (
  fetched: FetchedConfiguration,
  draft: UpdateCompanyConfigurationInput,
): UpdateCompanyConfigurationInput => {
  const input: UpdateCompanyConfigurationInput = { uuid: draft.uuid };
  for (const key of changedConfigurationKeys(fetched, draft)) {
    copyValue(key, draft, input);
  }
  return input;
};

type CompanyIdentity = { uuid: string; name: string };

export type CompanyOption = { value: string; label: string };

/**
 * The company picker's options. The cross-company `companies` list is
 * staff-only, so a customer's support account receives nothing from it. The
 * signed-in company is always offered, so that account can still open, and
 * save, its own configuration.
 */
export const companyOptionsWithSessionCompany = (
  companies: readonly CompanyIdentity[] | undefined,
  sessionCompany: CompanyIdentity | undefined,
): CompanyOption[] => {
  const options = (companies ?? []).map((company) => ({
    value: company.uuid,
    label: company.name,
  }));
  if (
    isNil(sessionCompany) ||
    options.some((option) => option.value === sessionCompany.uuid)
  ) {
    return options;
  }
  return [
    { value: sessionCompany.uuid, label: sessionCompany.name },
    ...options,
  ];
};

/**
 * Which query reads the selected company's configuration. The signed-in
 * company reads through the session-scoped `companyConfiguration`, which any
 * session may use. Every other company needs the cross-company
 * `companyConfigurationByCompanyUuid`, which only a Tenet staff session passes.
 * Saving is session-scoped either way, so only the signed-in company can be
 * saved.
 */
export const configurationReadSource = (
  selectedCompanyUuid: string,
  sessionCompanyUuid: string | undefined,
): "session" | "crossCompany" =>
  selectedCompanyUuid === sessionCompanyUuid ? "session" : "crossCompany";
